import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useAppDispatch } from '@/redux/rootReducer'
import { logout } from '@/redux/reducers/auth.reducer'

interface ReAuthDialogProps {
  open: boolean
  onClose: () => void
  title?: string
  message?: string
}

export const ReAuthDialog: React.FC<ReAuthDialogProps> = ({
  open,
  onClose,
  title,
  message,
}) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useAppDispatch()

  const handleContinueOffline = () => {
    console.log('User chose to continue offline')
    enqueueSnackbar(t('auth.continueOffline', 'Continuing in offline mode. You can sync later when reconnected.'), {
      variant: 'info',
      autoHideDuration: 5000
    })
    onClose()
  }



  const handleLogoutAndRelogin = () => {
    console.log('User chose to logout and re-login')
    enqueueSnackbar(t('auth.loggingOut', 'Logging out...'), { variant: 'info' })
    onClose()

    // Use the same logout mechanism as the OfflineHeader
    dispatch(logout())
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {title || t('auth.syncSessionExpired', 'Sync Requires Re-authentication')}
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          {message || t('auth.syncSessionExpiredMessage',
            'Your authentication session has expired while trying to sync your offline data. Choose how you would like to proceed:')}
        </DialogContentText>



        {/* Option 1: Continue Offline */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'grey.500', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              📱
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              {t('auth.continueOfflineTitle', 'Continue Offline')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('auth.continueOfflineDesc', 'Stay in offline mode and sync your data later when you have internet connection.')}
            </Typography>
          </Box>
        </Box>

        {/* Option 2: Logout and Re-login */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>
              🔐
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              {t('auth.logoutAndReloginTitle', 'Logout and Re-login')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('auth.logoutAndReloginDesc', 'Logout completely and go through the full login process to sync your data.')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleContinueOffline}
          variant="outlined"
          color="secondary"
          sx={{ minWidth: 140 }}
        >
          {t('auth.continueOffline', 'Continue Offline')}
        </Button>

        <Button
          onClick={handleLogoutAndRelogin}
          variant="contained"
          color="primary"
          sx={{ minWidth: 140 }}
        >
          {t('auth.logoutAndRelogin', 'Logout & Re-login')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReAuthDialog
